import React from "react";
import { motion } from "framer-motion";
import { Check, X, HelpCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SwipeOption, VoteType } from "./utils/types";
import { formatDate, formatTime } from "./utils/dateUtils";
import { logger } from "@core/lib/logger";

interface VoteOptionDesktopProps {
  option: SwipeOption;
  index: number;
  userVote: VoteType;
  userHasVoted: boolean;
  anyUserHasVoted: boolean;
  handleVote: (optionId: string, voteType: VoteType) => void;
  getStatsWithUser: (optionId: string) => {
    yes: number;
    maybe: number;
    no: number;
  };
  getExistingStats: (optionId: string) => {
    yes: number;
    maybe: number;
    no: number;
  };
  getRanking: (type: string) => Record<string, number> | number;
  totalVoters: number;
  allowMaybeVotes?: boolean;
}

const VoteOptionDesktop: React.FC<VoteOptionDesktopProps> = ({
  option,
  index,
  userVote,
  userHasVoted,
  anyUserHasVoted,
  handleVote,
  getStatsWithUser,
  getExistingStats,
  getRanking,
  totalVoters,
  allowMaybeVotes = true,
}) => {
  const { t } = useTranslation("polls");
  const rank = getRanking("all")[option.id];

  logger.debug("Option ranking", "vote", { optionId: option.id, rank });

  const stats = getStatsWithUser(option.id);
  const existingStats = getExistingStats(option.id);
  const totalVotants =
    existingStats.yes + existingStats.maybe + existingStats.no || 1;
  const dateLabel = option.date_group_label || formatDate(option.option_date);

  return (
    <div className="flex justify-center w-full">
      <table className="w-full max-w-2xl">
        <tbody>
          <tr className="border-b border-border/50 hover:bg-accent transition-colors">
            {/* Colonne Date */}
            <td className="px-4 py-3 min-w-[200px]">
              <div className="flex items-center gap-2.5">
                <div className="flex flex-col min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm text-foreground leading-tight">
                      {option.date_group_label ||
                        formatDate(option.option_date)}
                    </span>
                    {rank === 1 && totalVoters >= 2 && (
                      <span
                        className="text-warning text-sm"
                        title={t("vote.option.mostChosenSlot")}
                      >
                        ⭐
                      </span>
                    )}
                  </div>
                  {!option.date_group_label && option.time_slots && (
                    <span className="text-xs text-muted-foreground mt-0.5">
                      {formatTime(option.time_slots)}
                    </span>
                  )}
                </div>
              </div>
            </td>

            {/* Colonne Bouton OUI */}
            <td className="px-2 py-3 w-24">
              <button
                onClick={() => handleVote(option.id, "yes" as VoteType)}
                className={`w-full relative overflow-hidden rounded-md px-3 py-2.5 transition-all border-2 ${
                  userVote === "yes" && userHasVoted
                    ? "bg-success/30 border-success ring-1 ring-success"
                    : "bg-muted border-border hover:bg-accent hover:border-success/50"
                }`}
                data-testid={`vote-option-yes-${option.id}`}
                title={t("vote.option.yes")}
                aria-label={t("vote.option.yesAriaLabel", { date: dateLabel })}
                aria-pressed={userVote === "yes" && userHasVoted}
              >
                <div className="flex items-center justify-center gap-2">
                  <Check
                    className={`w-4 h-4 ${
                      userVote === "yes" && userHasVoted
                        ? "text-success"
                        : "text-success"
                    }`}
                  />
                  {stats.yes > 0 && (
                    <span
                      className={`text-xs font-bold ${
                        userVote === "yes" && userHasVoted
                          ? "text-success"
                          : "text-success"
                      }`}
                    >
                      {stats.yes}
                    </span>
                  )}
                </div>

                {/* Barre de progression horizontale */}
                {existingStats.yes > 0 && (
                  <motion.div
                    className="absolute bottom-0 left-0 h-1 bg-success/60"
                    initial={{ width: 0 }}
                    animate={{
                      width: `${(existingStats.yes / totalVotants) * 100}%`,
                    }}
                    transition={{ duration: 0.3 }}
                  />
                )}
              </button>
            </td>

            {/* Colonne Bouton PEUT-ÊTRE */}
            {allowMaybeVotes && (
              <td className="px-2 py-3 w-24">
                <button
                  onClick={() => handleVote(option.id, "maybe" as VoteType)}
                  className={`w-full relative overflow-hidden rounded-md px-3 py-2.5 transition-all border-2 ${
                    (userVote === "maybe" && userHasVoted) ||
                    (!userHasVoted && userVote === "maybe")
                      ? "bg-warning/30 border-warning ring-1 ring-warning"
                      : "bg-muted border-border hover:bg-accent hover:border-warning/50"
                  }`}
                  data-testid={`vote-option-maybe-${option.id}`}
                  title={t("vote.option.maybe")}
                  aria-label={t("vote.option.maybeAriaLabel", {
                    date: dateLabel,
                  })}
                  aria-pressed={
                    (userVote === "maybe" && userHasVoted) ||
                    (!userHasVoted && userVote === "maybe")
                  }
                >
                  <div className="flex items-center justify-center gap-2">
                    <HelpCircle
                      className={`w-4 h-4 ${
                        (userVote === "maybe" && userHasVoted) ||
                        (!userHasVoted && userVote === "maybe")
                          ? "text-warning"
                          : "text-warning"
                      }`}
                    />
                    {stats.maybe > 0 && (
                      <span
                        className={`text-xs font-bold ${
                          (userVote === "maybe" && userHasVoted) ||
                          (!userHasVoted && userVote === "maybe")
                            ? "text-warning"
                            : "text-warning"
                        }`}
                      >
                        {stats.maybe}
                      </span>
                    )}
                  </div>

                  {/* Barre de progression horizontale */}
                  {existingStats.maybe > 0 && (
                    <motion.div
                      className="absolute bottom-0 left-0 h-1 bg-warning/60"
                      initial={{ width: 0 }}
                      animate={{
                        width: `${(existingStats.maybe / totalVotants) * 100}%`,
                      }}
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </button>
              </td>
            )}

            {/* Colonne Bouton NON */}
            <td className="px-2 py-3 w-24">
              <button
                onClick={() => handleVote(option.id, "no" as VoteType)}
                className={`w-full relative overflow-hidden rounded-md px-3 py-2.5 transition-all border-2 ${
                  userVote === "no" && userHasVoted
                    ? "bg-destructive/30 border-destructive ring-1 ring-destructive"
                    : "bg-muted border-border hover:bg-accent hover:border-destructive/50"
                }`}
                data-testid={`vote-option-no-${option.id}`}
                title={t("vote.option.no")}
                aria-label={t("vote.option.noAriaLabel", { date: dateLabel })}
                aria-pressed={userVote === "no" && userHasVoted}
              >
                <div className="flex items-center justify-center gap-2">
                  <X
                    className={`w-4 h-4 ${
                      userVote === "no" && userHasVoted
                        ? "text-destructive"
                        : "text-destructive"
                    }`}
                  />
                  {stats.no > 0 && (
                    <span
                      className={`text-xs font-bold ${
                        userVote === "no" && userHasVoted
                          ? "text-destructive"
                          : "text-destructive"
                      }`}
                    >
                      {stats.no}
                    </span>
                  )}
                </div>

                {/* Barre de progression horizontale */}
                {existingStats.no > 0 && (
                  <motion.div
                    className="absolute bottom-0 left-0 h-1 bg-destructive/60"
                    initial={{ width: 0 }}
                    animate={{
                      width: `${(existingStats.no / totalVotants) * 100}%`,
                    }}
                    transition={{ duration: 0.3 }}
                  />
                )}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default VoteOptionDesktop;
